import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, Typography, Autocomplete } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearBalanzas = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    balcodigo: "",
    baldescri: "",
    balip: "",
    balclave: "",
    balimpresora: "",
    loccodigo: "",
  })

  // --- LÓGICA DEFENSIVA PARA LOCALIDADES ---
  // Reutilizamos el endpoint que ya trae las localidades de la empresa
  const { data: rawLocalidades, isLoading: isLoadingLoc } = useQuery({
    queryKey: ["listaLocalidadesBalanzas"],
    queryFn: async () => {
      try {
        const response = await api.get("/Balanzas/getListaLocalidades")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Localidades:", error)
        return []
      }
    },
  })

  const locValidadas = Array.isArray(rawLocalidades)
    ? rawLocalidades
    : Array.isArray(rawLocalidades?.data)
      ? rawLocalidades.data
      : []
  const listaLocalidades = locValidadas.map((item) => ({
    id: item.loccodigo || item.id,
    label: item.label || `${item.loccodigo} - ${item.locdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  const {
    mutateAsync: SaveCreacionBalanza,
    isPending: isSavingCreacionBalanza,
    isError,
  } = useMutation({
    queryKey: ["isCreatingBalanza"],
    fn: async (data) => {
      const response = await api.post("/Balanzas/createBalanzas", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    // Excepción: IP y Clave no se fuerzan a mayúsculas para no corromper credenciales
    if (field === "balip" || field === "balclave") {
      setFormData((prev) => ({ ...prev, [field]: value }))
    } else {
      setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }))
    }
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!formData.loccodigo.trim()) return showWarning("Debe seleccionar una Localidad")
    if (!formData.balcodigo.trim()) return showWarning("El Código de Balanza es obligatorio")
    if (!formData.baldescri.trim()) return showWarning("La Descripción es obligatoria")

    await SaveCreacionBalanza(formData)
  }

  // Se busca específicamente la acción GRABAR
  const grabarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")
  const toolbarActions = grabarAction
    ? [
        {
          label: grabarAction.acccaption,
          key: grabarAction.acccaption,
          icon: getIconComponent(grabarAction.accnameicono, grabarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingCreacionBalanza || isLoadingLoc}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nueva Balanza</b>
        </div>
        <CustomBackdrop isLoading={isSavingCreacionBalanza || isLoadingLoc} />
        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de la Balanza
            </Typography>
            <Grid container spacing={3}>
              {/* FILA 1: Localidad, Código y Descripción */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaLocalidades}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaLocalidades.find((c) => c.id === formData.loccodigo) ||
                    (formData.loccodigo ? { id: formData.loccodigo, label: `${formData.loccodigo}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("loccodigo", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Localidad *"
                      placeholder="Buscar..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.loccodigo}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.balcodigo}
                  onChange={(e) => handleInputChange("balcodigo", e.target.value)}
                  error={isError && !formData.balcodigo}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.baldescri}
                  onChange={(e) => handleInputChange("baldescri", e.target.value)}
                  error={isError && !formData.baldescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* FILA 2: IP, Clave e Impresora */}
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Dirección IP"
                  value={formData.balip}
                  onChange={(e) => handleInputChange("balip", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="password"
                  label="Clave"
                  value={formData.balclave}
                  onChange={(e) => handleInputChange("balclave", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Impresora"
                  value={formData.balimpresora}
                  onChange={(e) => handleInputChange("balimpresora", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearBalanzas
